import shortid from 'shortid';
import { TitleBar } from '@shopify/app-bridge/actions';
import { ToastConfig } from './useToast';

export enum ActionType {
  LoadingStart = 'LOADING_START',
  LoadingStop = 'LOADING_STOP',
  ToastPush = 'TOAST_PUSH',
  ToastPop = 'TOAST_POP',
  TitleBarUpdate = 'TITLE_BAR_UPDATE',
  SaveBarShow = 'SAVE_BAR_SHOW',
  SaveBarHide = 'SAVE_BAR_HIDE',
  SaveBarDisable = 'SAVE_BAR_DISABLE',
  SaveBarEnable = 'SAVE_BAR_ENABLE',
}

// LOADING

interface LoadingStartAction {
  type: ActionType.LoadingStart;
  payload: {
    id: string;
  };
}

interface LoadingStopAction {
  type: ActionType.LoadingStop;
  payload: {
    id: string;
  };
}

// TOAST
interface Toast {
  id: string;
  message: string;
  error: boolean;
  duration: number;
  priority: number;
}

interface ToastPushAction {
  type: ActionType.ToastPush;
  payload: {
    toast: ToastConfig;
  };
}

interface ToastPopAction {
  type: ActionType.ToastPop;
  payload: {
    id: string;
  };
}

// TITLE BAR
interface TitleBarUpdateAction {
  type: ActionType.TitleBarUpdate;
  payload: TitleBar.Options;
}

// SAVE BAR
interface SaveBarShowAction {
  type: ActionType.SaveBarShow;
}

interface SaveBarHideAction {
  type: ActionType.SaveBarHide;
}

interface SaveBarDisableAction {
  type: ActionType.SaveBarDisable;
}

interface SaveBarEnableAction {
  type: ActionType.SaveBarEnable;
}

export type Action =
  | LoadingStartAction
  | LoadingStopAction
  | ToastPushAction
  | ToastPopAction
  | TitleBarUpdateAction
  | SaveBarShowAction
  | SaveBarHideAction
  | SaveBarDisableAction
  | SaveBarEnableAction;

interface State {
  /**
   * Overall global loading state
   */
  loading: boolean;
  /**
   * Contains ids of loading components
   */
  loadingQueue: string[];
  /**
   * List of toasts to show
   */
  toastQueue: Toast[];
  /**
   * Options for the titleBar
   */
  titleBarOptions: TitleBar.Options;
}

export const initialState: State = {
  loading: false,
  loadingQueue: [],
  toastQueue: [],
  titleBarOptions: {
    title: undefined,
    buttons: undefined,
    breadcrumbs: undefined,
  },
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.LoadingStart: {
      const { id } = action.payload;

      if (state.loading === true && state.loadingQueue.includes(id)) {
        return state;
      }

      return {
        ...state,
        loading: true,
        loadingQueue: [...state.loadingQueue.filter(i => i !== id), id],
      };
    }
    case ActionType.LoadingStop: {
      const { id } = action.payload;

      if (!state.loadingQueue.includes(id)) {
        return state;
      }

      const loadingQueue = state.loadingQueue.filter(i => i !== id);

      return { ...state, loading: loadingQueue.length > 0, loadingQueue };
    }
    case ActionType.ToastPush: {
      const id = shortid.generate();
      const toast: Toast = {
        id,
        error: false,
        duration: 2000,
        priority: action.payload.toast.error ? 1 : 0,
        ...action.payload.toast,
      };

      // Handle priority
      const toastQueue = [...state.toastQueue, toast].sort(
        (a, b) => b.priority - a.priority,
      );

      return { ...state, toastQueue };
    }
    case ActionType.ToastPop: {
      const { id } = action.payload;

      return {
        ...state,
        toastQueue: state.toastQueue.filter(t => t.id !== id),
      };
    }
    case ActionType.TitleBarUpdate: {
      return {
        ...state,
        titleBarOptions: { ...action.payload },
      };
    }
    default:
      throw new Error(`Invalid action type '${(action as any).type}'`);
  }
}

export default reducer;
